import React, { useState } from "react"
import i18n from "../../../../services/i18n"
import { Button, Input, Row, Col, Space, Typography, message, Alert } from "antd"
import { Download, RefreshCw, Send, Database, CheckCircle } from "react-feather"
import { useMutation, useQuery } from "@tanstack/react-query"
import { GET_BACKUP } from "../../../../rest-api/queries/runBackup"
import { BACKUP_AND_SEND_EMAIL } from "../../../../rest-api/queries/emailSettings"
import { request as apiRequest } from "../../../../utils/config"

export default function DatabaseBackup() {
  const [targetEmail, setTargetEmail] = useState<string>(() => {
    return localStorage.getItem("crm_user_email") || "admin@zenith-erp.uz"
  })
  const [backupFile, setBackupFile] = useState<string>("")

  const {
    refetch: runBackupQuery,
    isFetching: creatingBackup,
  } = useQuery(["GET_BACKUP"], GET_BACKUP, {
    enabled: false,
    onSuccess: (res: any) => {
      const fileName = typeof res === "string" ? res : res?.fileName || res?.data || ""
      setBackupFile(fileName)
      message.success(i18n.t("msgBackupCreated"))
    },
    onError: (err: any) => {
      message.error(i18n.t("msgBackupFailed") + ": " + err.message)
    },
  })

  const { mutate: sendBackupEmail, isLoading: sendingEmail } = useMutation(BACKUP_AND_SEND_EMAIL, {
    onSuccess: (res: any) => {
      const fileName = res?.fileName || res?.FileName || ""
      if (fileName) setBackupFile(fileName)
      message.success(res?.message || "Baza zaxira nusxasi emailga yuborildi!")
    },
    onError: (err: any) => {
      message.error(err?.response?.data?.message || err.message || "Emailga yuborishda xatolik yuz berdi")
    },
  })

  const handleSendToEmail = () => {
    if (!targetEmail.trim()) {
      message.warning(i18n.t("msgEnterRecipientEmail"))
      return
    }
    sendBackupEmail({ targetEmail: targetEmail.trim() })
  }

  /*
    Kunlik avtomatik zaxirani QO'LDA sinash. Avtomatik yurish har kuni
    soat 23 da bo'ladi; bu tugma esa "ertagacha kutmasdan hozir ko'rish"
    uchun. Natija hisoboti to'g'ridan-to'g'ri ekranda ko'rsatiladi —
    Telegram va email holati ham ichida bo'ladi.
  */
  const [dailyReport, setDailyReport] = useState<string>("")
  const { mutate: runDaily, isLoading: dailyRunning } = useMutation(
    async () => {
      const { request } = await import("../../../../utils/config")
      const res = await request({
        url: "Helpers/RunDailyBackup",
        method: "POST",
      })
      return res.data
    },
    {
      onSuccess: (res: any) => {
        setDailyReport(res?.report || "")
        message.success(i18n.t("msgDailyBackupDone"))
      },
      onError: (err: any) => {
        message.error(err?.response?.data?.message || err.message)
      },
    }
  )

  /*
    Zaxira faylini yuklab olish. Ilgari ochiq /media/{fayl} havolasi
    ishlatilardi — u kirishsiz ochiq edi, ya'ni butun baza nusxasini
    nomini bilgan har kim yuklab olardi. Endi fayl token bilan, faqat
    tizim egasiga ochiq Helpers/DownloadBackup orqali olinadi.
  */
  const [downloading, setDownloading] = useState(false)
  const handleDownload = async () => {
    if (!backupFile) return
    setDownloading(true)
    try {
      const res: any = await apiRequest({
        url: "Helpers/DownloadBackup",
        params: { fileName: backupFile },
        responseType: "blob",
      })
      const blob: Blob = res?.data instanceof Blob ? res.data : new Blob([res?.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = backupFile
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    } catch (err: any) {
      message.error(i18n.t("msgBackupFailed") + ": " + (err?.message || ""))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div style={{ padding: "4px 0" }}>
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Input
            placeholder={i18n.t("phRecipientEmailExample")}
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            style={{ borderRadius: 6, flex: 1 }}
          />
          <Button
            type="primary"
            icon={<Send size={15} style={{ marginRight: 6 }} />}
            onClick={handleSendToEmail}
            loading={sendingEmail}
            style={{
              borderRadius: 6,
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              borderColor: "#10B981",
              fontWeight: 600,
            }}
          >
            Emailga yuborish
          </Button>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Button
            icon={<RefreshCw size={15} style={{ marginRight: 6 }} />}
            onClick={() => runBackupQuery()}
            loading={creatingBackup}
            style={{ borderRadius: 6 }}
          >
            Zaxira nusxasini shakllantirish
          </Button>

          {backupFile && (
            <Button
              type="default"
              icon={<Download size={15} style={{ marginRight: 6 }} />}
              onClick={handleDownload}
              loading={downloading}
              style={{
                borderRadius: 6,
                color: "#2563EB",
                borderColor: "#2563EB",
                fontWeight: 600,
              }}
            >
              Faylni yuklab olish ({backupFile})
            </Button>
          )}
        </div>

        {backupFile && (
          <Alert
            message={`Oxirgi zaxira nusxasi: ${backupFile}`}
            type="success"
            showIcon
            style={{ borderRadius: 8, fontSize: 12 }}
          />
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Button
            icon={<Database size={15} style={{ marginRight: 6 }} />}
            onClick={() => runDaily()}
            loading={dailyRunning}
            style={{ borderRadius: 6 }}
          >
            {i18n.t("btnTestDailyBackup")}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {i18n.t("dailyBackupHint")}
          </Typography.Text>
        </div>

        {dailyReport && (
          <Alert
            message={dailyReport}
            type="info"
            showIcon
            style={{ borderRadius: 8, fontSize: 12 }}
          />
        )}
      </Space>
    </div>
  )
}
